import { BehaviorSubject, firstValueFrom } from "rxjs";

const NON_PROD_ENV_NAMES = new Set([
  "dev",
  "development",
  "default-dev",
  "stage",
  "staging",
  "demo",
  "local",
]);

const MFA_ENABLED_ENV_NAMES = new Set([
  "prod",
  "production",
  "testing",
  "test",
  "default-testing",
  "landis-testing",
]);

const mfaPromptActiveSource = new BehaviorSubject(false);
let pendingMfaProfileChecks = 0;
let openMfaPrompts = 0;

export const mfaPromptActive$ = mfaPromptActiveSource.asObservable();

function publishMfaPromptState(): void {
  mfaPromptActiveSource.next(pendingMfaProfileChecks > 0 || openMfaPrompts > 0);
}

function completeMfaProfileCheck(): void {
  pendingMfaProfileChecks = Math.max(0, pendingMfaProfileChecks - 1);
  publishMfaPromptState();
}

interface MfaPromptModalRef {
  result: Promise<unknown>;
}

/** Register an MFA prompt after its modal has actually been opened. */
export function notifyMfaPromptOpened(modalRef?: MfaPromptModalRef): void {
  openMfaPrompts += 1;
  publishMfaPromptState();

  // A modal can be closed by any action (including navigation to the MFA
  // settings page), so release the prompt state from the modal lifecycle.
  modalRef?.result.then(
    () => notifyMfaPromptClosed(),
    () => notifyMfaPromptClosed()
  );
}

export function notifyMfaPromptClosed(): void {
  openMfaPrompts = Math.max(0, openMfaPrompts - 1);
  publishMfaPromptState();
}

function normalize(value?: string | null): string {
  return (value || "").toLowerCase().trim();
}

export function normalizeMfaEnabled(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "enabled", "enable", "active"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "disabled", "disable", "inactive"].includes(normalized)) {
      return false;
    }
  }

  return null;
}

export function isNonProdHostname(hostname?: string | null): boolean {
  const host = normalize(hostname);
  if (!host) {
    return false;
  }

  return (
    host === "localhost" ||
    host.includes("dev") ||
    host.includes("test") ||
    host.includes("testing") ||
    host.includes("stage") ||
    host.includes("staging")
  );
}

export function shouldEnforceMfaByEnvironment(
  envName?: string | null,
  hostname?: string | null
): boolean {
  const environment = normalize(envName);

  // MFA is intentionally limited to production and testing deployments.
  if (MFA_ENABLED_ENV_NAMES.has(environment)) {
    return true;
  }

  if (NON_PROD_ENV_NAMES.has(environment)) {
    return false;
  }

  if (isNonProdHostname(hostname)) {
    return false;
  }

  return false;
}

export function hasActiveMfaSkipWindow(
  skipMfaEndDate: unknown,
  now = Date.now()
): boolean {
  if (typeof skipMfaEndDate !== "string" || !skipMfaEndDate.trim()) {
    return false;
  }

  const raw = skipMfaEndDate.trim();
  const skipUntil = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? new Date(`${raw}T23:59:59.999Z`)
    : new Date(raw);

  return !Number.isNaN(skipUntil.getTime()) && now <= skipUntil.getTime();
}

export function shouldShowMfaChallenge(
  enforceMfaPolicy: boolean,
  hasPendingResolver: boolean,
  mfaEnabled: unknown,
  skipMfaEndDate: unknown,
  now = Date.now()
): boolean {
  if (hasActiveMfaSkipWindow(skipMfaEndDate, now)) {
    return false;
  }

  if (hasPendingResolver) {
    return true;
  }

  // A TOTP sign-in screen is usable only when Firebase supplied a resolver.
  // Backend MFA state is handled separately as an enrollment requirement.
  return false;
}

export function withFirebaseUid<T extends Record<string, unknown>>(
  payload: T,
  firebaseUid?: string | null
): T & { firebase_uid?: string } {
  const normalizedUid = firebaseUid?.trim();
  if (!normalizedUid) {
    return payload;
  }

  return {
    ...payload,
    firebase_uid: normalizedUid,
  };
}

export async function resolveMfaEnabledFromProfile(
  apiRequest: {
    apiRequest: (url: string, mockFile: string, body?: unknown) => unknown;
  },
  userId?: number | null,
  fallbackEnabled = false
): Promise<boolean> {
  pendingMfaProfileChecks += 1;
  publishMfaPromptState();

  const finish = (mfaEnabled: boolean): boolean => {
    completeMfaProfileCheck();
    return mfaEnabled;
  };

  const id =
    typeof userId === "number" && Number.isFinite(userId) ? userId : undefined;

  if (!id) {
    return finish(fallbackEnabled);
  }

  try {
    const response: any = await firstValueFrom(
      apiRequest.apiRequest("users/profile/", "profile.json", {
        payload: { userID: id },
      }) as any
    );
    const profileMfaEnabled = response?.data?.profile?.mfaEnabled;
    if (profileMfaEnabled === undefined || profileMfaEnabled === null) {
      return finish(fallbackEnabled);
    }
    if (typeof profileMfaEnabled === "boolean") {
      return finish(profileMfaEnabled);
    }
    return finish(String(profileMfaEnabled).toLowerCase().trim() === "true");
  } catch {
    return finish(fallbackEnabled);
  }
}
